import { createRouter, createWebHistory } from "vue-router";

const SITE_URL = "https://beeforbeer.com";
const DEFAULT_META = {
  title: "beeApp | get the best beer around you",
  description: "Discover top-rated beers and breweries near you with beeApp.",
  canonicalPath: "/",
  noindex: false,
};

// Route-based code splitting - each page loads only when needed
const LandingPage = () => import("@/pages/LandingPage.vue");
const ExperienceUser = () => import("@/pages/ExperienceUser.vue");
const ExperienceBrewery = () => import("@/pages/ExperienceBrewery.vue");
const AboutPage = () => import("@/pages/AboutPage.vue");
const ContactUs = () => import("@/pages/ContactUs.vue");
const PricingPage = () => import("@/pages/PricingPage.vue");
const OnBoarding = () => import("@/pages/OnBoarding.vue");
const NotFound = () => import("@/pages/NotFound.vue");

const routes = [
  {
    path: "/",
    name: "LandingPage",
    component: LandingPage,
    meta: {
      title: "beeApp | get the best beer around you",
      description:
        "Discover top-rated beers and breweries near you with beeApp.",
      canonicalPath: "/",
    },
  },
  {
    path: "/experience-user",
    name: "ExperienceUser",
    component: ExperienceUser,
    meta: {
      title: "Experience beeApp as a User",
      description:
        "Discover beers, connect with enthusiasts, and find top brewery experiences using beeApp.",
      canonicalPath: "/experience-user",
    },
  },
  {
    path: "/experience-brewery",
    name: "ExperienceBrewery",
    component: ExperienceBrewery,
    meta: {
      title: "Experience beeApp as a Brewery",
      description:
        "See how beeApp helps breweries with events, offers, ratings, and actionable insights.",
      canonicalPath: "/experience-brewery",
    },
  },
  {
    path: "/contact",
    name: "Contact",
    component: ContactUs,
    meta: {
      title: "Contact beeApp",
      description:
        "Contact the beeApp team for partnership, onboarding, or support inquiries.",
      canonicalPath: "/contact",
    },
  },
  {
    path: "/contactus",
    redirect: "/contact",
  },
  {
    path: "/about",
    name: "AboutPage",
    component: AboutPage,
    meta: {
      title: "About beeApp",
      description:
        "Learn about the beeApp mission and the team building better brewery experiences.",
      canonicalPath: "/about",
    },
  },
  {
    path: "/pricing",
    name: "PricingPage",
    component: PricingPage,
    alias: ["/price"],
    meta: {
      title: "beeApp Pricing",
      description:
        "Review beeApp plans for breweries, including Free, Starter, and Growth options.",
      canonicalPath: "/pricing",
    },
  },
  {
    path: "/onboarding",
    name: "OnBoarding",
    component: OnBoarding,
    alias: ["/onboard", "/brewery-onboard", "/brewerysignup"],
    meta: {
      title: "Onboard Your Brewery | beeApp",
      description:
        "Start onboarding your brewery with beeApp using secure business details and contact verification.",
      canonicalPath: "/onboarding",
    },
  },
  {
    path: "/:catchAll(.*)",
    name: "NotFound",
    component: NotFound,
    meta: {
      title: "Page Not Found | beeApp",
      description: "The page you requested could not be found.",
      canonicalPath: "/",
      noindex: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    const app = document.getElementById("app");
    if (app && app.scrollIntoView) {
      app.scrollIntoView({ behavior: "smooth" });
    }
  },
});

function normalizePath(path) {
  if (!path) return "/";
  if (path === "/") return "/";
  return path.startsWith("/") ? path.replace(/\/+$/, "") : `/${path}`;
}

function upsertMetaByName(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertMetaByProperty(property, content) {
  let tag = document.head.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function applySeoMeta(route) {
  const meta = { ...DEFAULT_META, ...(route.meta || {}) };
  const canonicalPath = normalizePath(meta.canonicalPath || route.path || "/");
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const robots = meta.noindex ? "noindex,nofollow" : "index,follow";

  document.title = meta.title;
  upsertMetaByName("description", meta.description);
  upsertMetaByName("robots", robots);
  upsertCanonical(canonicalUrl);

  upsertMetaByProperty("og:title", meta.title);
  upsertMetaByProperty("og:description", meta.description);
  upsertMetaByProperty("og:type", "website");
  upsertMetaByProperty("og:url", canonicalUrl);

  upsertMetaByName("twitter:title", meta.title);
  upsertMetaByName("twitter:description", meta.description);
}

router.afterEach((to) => {
  applySeoMeta(to);
});

export default router;
